import { Injectable, NgZone } from '@angular/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Platform } from '@ionic/angular';
import { Observable, Subject } from 'rxjs';
import { errorMessageOf } from 'src/app/common/logging/error-normalization';

import { EnvironmentService } from '../shared/services/environment/environment.service';
import { NetworkInfoService } from '../shared/services/network/network-info.service';
import { FusionAuthClient, OAuthError, OAuthUnavailableError } from './fusion-auth-client';
import { logSessionEvent } from './session/session-log';
import { ITokenSet } from './token-set';
import { TokenState, TokenStore } from './token-store';

export type SignInResult =
  | { kind: 'signedIn' }
  /** web: the page navigates to the identity provider, the result arrives in the next process */
  | { kind: 'redirected' }
  /** mobile: the user closed the system browser without finishing */
  | { kind: 'cancelled' }
  /** the device has no network: nothing was opened, the user is told to try again online */
  | { kind: 'offline' }
  | { kind: 'failed'; error: unknown };

export type RefreshResult =
  | { kind: 'refreshed'; token: ITokenSet }
  /** the authorization server rejected the grant, the session is over */
  | { kind: 'rejected'; reason: string }
  /** transient: no network, timeout, identity provider unreachable; the stored token is kept */
  | { kind: 'unavailable'; reason: string };

export { TokenState };

/** What has to survive the browser round trip: the `state` to verify, the PKCE verifier, the target. */
interface IPendingSignIn {
  state: string;
  codeVerifier: string;
  returnUrl: string | null;
}

type RoundTripOutcome = SignInResult | 'done' | 'cancelled';
/** The sign-in or sign-out currently going through the system browser (mobile). */
interface IRoundTrip {
  callbackReceived: boolean;
  resolve: (outcome: RoundTripOutcome) => void;
}

const MOBILE_REDIRECT_URI = 'appauth://callback';
const MOBILE_POST_LOGOUT_URI = 'appauth://endsession';
/**
 * localStorage on purpose: the record has to survive the browser round trip to FusionAuth, and
 * sessionStorage did not do that reliably in the field. It is single-use (removed on read) and
 * bound to the `state` of the redirect, so a stale record is harmless.
 */
const PENDING_SIGN_IN_KEY = 'alberta.auth.pendingSignIn';
/** How long after the system browser closed its callback may still arrive (the two events come in either order). */
const BROWSER_CLOSE_GRACE_MS = 1_500;

/**
 * The app's OAuth client, callable like a normal async API on both platforms.
 *
 * It owns the tokens (`currentToken`, persisted through the `TokenStore`), the redirect URIs and
 * the browser round trip to FusionAuth; the protocol itself is the `FusionAuthClient`. What it
 * does not do: decide anything about the session. When a token is due, what a refusal means,
 * when the socket is authenticated, when to log out — that is the `SessionController`, the only
 * caller of `login()`, `endSession()`, `refresh()` and `clear()`. The one other caller is the
 * `APP_INITIALIZER`, which calls `captureCallbackUrl()` on the web before Angular's router
 * replaces the redirect URL.
 *
 * | | web | mobile |
 * |---|---|---|
 * | browser | page redirect | system browser (`@capacitor/browser`) |
 * | redirect URLs | `window.location.origin` for login and logout | `appauth://callback`, `appauth://endsession` |
 * | callback | captured from the URL of the *next* process, completed in `initialize()` | `appUrlOpen` event in the same process, or in a new one when the OS killed the app meanwhile |
 * | `login()` | the page navigates away → `redirected` | settles with the callback, or `cancelled` |
 */
@Injectable({ providedIn: 'root' })
export class AuthClient {
  private _currentToken: ITokenSet | undefined;
  private _initialized: Promise<void> | null = null;
  private _listening = false;
  private _callbackUrl: string | null = null;
  private _postLoginTarget: string | null = null;
  private readonly _unsolicitedSignIn$ = new Subject<void>();
  private _roundTrip: IRoundTrip | null = null;

  /**
   * A sign-in completed that nobody was waiting for: the OS killed the app while the system
   * browser was open and the callback arrived in a new process. The `SessionController` listens.
   */
  public readonly unsolicitedSignIn$: Observable<void> = this._unsolicitedSignIn$.asObservable();

  constructor(
    private readonly _oauth: FusionAuthClient,
    private readonly _store: TokenStore,
    private readonly _platform: Platform,
    private readonly _environment: EnvironmentService,
    private readonly _ngZone: NgZone,
    private readonly _network: NetworkInfoService
  ) {}

  /** Mobile asks the Capacitor network plugin; the web trusts the browser only when it says offline. */
  private get isOffline(): boolean {
    return this.isHybrid ? this._network.isNotConnected : navigator.onLine === false;
  }

  private get isHybrid(): boolean {
    return this._platform.is('hybrid');
  }

  /** Where FusionAuth sends the browser after the login; registered as redirect URL in FusionAuth. */
  public get redirectUri(): string {
    return this.isHybrid ? MOBILE_REDIRECT_URI : window.location.origin;
  }

  /** Where FusionAuth sends the browser after the logout; registered as logout URL in FusionAuth. */
  public get postLogoutRedirectUri(): string {
    return this.isHybrid ? MOBILE_POST_LOGOUT_URI : window.location.origin;
  }

  /** The token currently loaded, whether or not it is still valid. */
  public get currentToken(): ITokenSet | undefined {
    return this._currentToken;
  }

  /** What the storage said when it was read; `unknown` while uninitialised or when the read failed. */
  public get tokenState(): TokenState {
    return this._currentToken ? 'token-present' : this._store.lastReadState;
  }

  /** Web only, to be called from the APP_INITIALIZER: remembers the redirect before the router replaces the URL. */
  public captureCallbackUrl(): void {
    if (!this.isHybrid && hasAuthorizationResponse(window.location.href)) {
      this._callbackUrl = window.location.href;
    }
  }

  /**
   * Loads the persisted token and finishes a web sign-in that came back through the redirect.
   * Single-flight, and done once — unless the storage did not answer, then the next call reads again.
   */
  public initialize(): Promise<void> {
    if (!this._initialized) {
      this._initialized = this.runInitialize().finally(() => {
        if (this._store.lastReadState === 'unknown') {
          this._initialized = null;
        }
      });
    }
    return this._initialized;
  }

  /** Opens the identity provider. Mobile: settles with the callback or the closed browser. Web: the page navigates away. */
  public async login(extras: Record<string, string>, returnUrl: string | null = null): Promise<SignInResult> {
    await this.initialize();
    if (this.isOffline) {
      // Opening the identity provider without a network only shows the browser's error page. The
      // previous library refused here as a side effect of fetching its discovery document; this
      // is that refusal made explicit. An expected outcome: a breadcrumb, no event.
      window.logger?.addBreadcrumb({ category: 'auth', message: '[AuthClient] sign-in refused: device offline' });
      return { kind: 'offline' };
    }
    let url: string;
    try {
      const request = await this._oauth.createAuthorizeRequest(this.redirectUri, {
        loginHint: extras.login_hint,
        prompt: extras.prompt,
      });
      this.savePendingSignIn({ state: request.state, codeVerifier: request.codeVerifier, returnUrl });
      url = request.url;
    } catch (error) {
      this.reportSignInFailure(error);
      return { kind: 'failed', error };
    }

    if (!this.isHybrid) {
      window.location.assign(url);
      return { kind: 'redirected' };
    }

    const outcome = await this.roundTrip(url);
    if (outcome === 'cancelled') {
      // The pending record stays. On Android `browserFinished` is a heuristic (tab hidden + app
      // resumed) that also fires when the user leaves the app during the login, e.g. for the
      // password manager; the callback that still arrives afterwards must find the record and
      // completes the sign-in through `unsolicitedSignIn$`. The record is single-use and bound to
      // `state`, and the next `login()` overwrites it, so keeping it costs nothing.
      return { kind: 'cancelled' };
    }
    const result = outcome === 'done' ? { kind: 'failed' as const, error: new Error('unexpected callback') } : outcome;
    if (result.kind === 'failed') {
      this.reportSignInFailure(result.error);
    }
    return result;
  }

  /** Where the user wanted to go before the sign-in; `null` when nowhere in particular. Consumed on read. */
  public consumePostLoginTarget(): string | null {
    const target = this._postLoginTarget;
    this._postLoginTarget = null;
    return target;
  }

  /**
   * Ends the session at the identity provider. Mobile: settles with the callback or the closed
   * browser. Web: the page navigates away. The token is the caller's business (`clear()`).
   */
  public async endSession(): Promise<void> {
    await this.initialize();
    const url = this._oauth.logoutUrl(this.postLogoutRedirectUri);
    if (!this.isHybrid) {
      window.location.assign(url);
      return;
    }
    await this.roundTrip(url);
  }

  /** Forgets the token without talking to the identity provider. */
  public async clear(): Promise<void> {
    this._currentToken = undefined;
    await this._store.clear();
  }

  /**
   * Exchanges the refresh token for a new token set and reports what happened, never throws.
   *
   * Several web tabs share the store but each holds its own copy of the token. Before spending a
   * refresh token this method therefore looks at the store: a token another tab already refreshed
   * is adopted instead of racing it, and a rejected grant only ends the session when the store
   * still holds the very refresh token that was rejected.
   */
  public async refresh(): Promise<RefreshResult> {
    const current = this._currentToken;
    const refreshToken = current?.refreshToken;
    if (!current || !refreshToken) {
      return { kind: 'unavailable', reason: 'no refresh token loaded' };
    }

    const stored = await this.storedTokenIfNewer(current);
    if (stored) {
      this._currentToken = stored;
      return { kind: 'refreshed', token: stored };
    }

    try {
      const fresh = await this._oauth.refresh(refreshToken);
      if (!this._currentToken) {
        // cleared while the request was running (logout): the result belongs to a session that is over
        return { kind: 'unavailable', reason: 'logged out during the refresh' };
      }
      // RFC 6749 §6: a refresh response may leave the refresh token (and the id token) out, which
      // means "keep the old one"; FusionAuth does that with reusable refresh tokens
      const token: ITokenSet = {
        ...fresh,
        refreshToken: fresh.refreshToken ?? refreshToken,
        idToken: fresh.idToken ?? current.idToken ?? null,
      };
      this._currentToken = token;
      await this.persist(token);
      return { kind: 'refreshed', token };
    } catch (error) {
      if (error instanceof OAuthError && error.code === 'invalid_grant') {
        const adopted = await this.storedTokenIfNewer(current);
        if (adopted) {
          this._currentToken = adopted;
          return { kind: 'refreshed', token: adopted };
        }
        await this.clear();
        return { kind: 'rejected', reason: error.message };
      }
      return { kind: 'unavailable', reason: errorMessageOf(error) };
    }
  }

  //#region initialisation and callbacks

  private async runInitialize(): Promise<void> {
    await this._environment.ready;
    this._currentToken = (await this._store.load()) ?? undefined;
    // listeners after the load: a callback arriving in a fresh process (the OS killed the app
    // under the system browser) must not be overwritten by the load, and Capacitor retains the
    // launch URL until a listener takes it
    if (this.isHybrid) {
      this.listenForAppUrls();
    }

    if (this._callbackUrl) {
      const callbackUrl = this._callbackUrl;
      this._callbackUrl = null;
      // the code is single-use; leave a clean URL behind
      window.history.replaceState(null, '', window.location.pathname);
      const result = await this.completeSignIn(callbackUrl);
      if (result.kind === 'failed') {
        this.reportSignInFailure(result.error);
      }
    }
  }

  /** Turns the identity provider's redirect into tokens. Never throws. */
  private async completeSignIn(callbackUrl: string): Promise<SignInResult> {
    const params = new URL(callbackUrl).searchParams;
    const pending = this.takePendingSignIn();
    try {
      if (params.get('error')) {
        throw new OAuthError(params.get('error'), params.get('error_description') ?? undefined);
      }
      const code = params.get('code');
      if (!code || !pending) {
        throw new Error('no sign-in is pending');
      }
      if (params.get('state') !== pending.state) {
        throw new Error('state does not match');
      }
      const token = await this._oauth.exchangeCode(code, pending.codeVerifier, this.redirectUri);
      this._currentToken = token;
      this._postLoginTarget = pending.returnUrl ?? null;
      await this.persist(token);
      window.logger?.addBreadcrumb({ category: 'auth', message: '[AuthClient] tokens received' });
      return { kind: 'signedIn' };
    } catch (error) {
      return { kind: 'failed', error };
    }
  }

  /**
   * The token in memory is the truth of this process; the store only carries it to the next one.
   * A failing write must not undo a refresh or a sign-in that already happened: with rotating
   * refresh tokens the one in the store is already spent and would end the session on the next
   * refresh (#26615).
   */
  private async persist(token: ITokenSet): Promise<void> {
    try {
      await this._store.save(token);
    } catch (error) {
      logSessionEvent('session_anomaly', 'token_persist_failed', errorMessageOf(error), error);
    }
  }

  /**
   * One place for both platforms. A sign-in the user cancelled at the identity provider is not a
   * failure worth an event; everything else is counted by the identity provider's error code.
   */
  private reportSignInFailure(error: unknown): void {
    const cause = signInFailureCause(error);
    if (cause === 'access_denied') {
      window.logger?.addBreadcrumb({
        category: 'auth',
        message: '[AuthClient] sign-in cancelled at the identity provider',
      });
      return;
    }
    logSessionEvent('sign_in_failed', cause, errorMessageOf(error), error);
  }

  /** A token another tab saved after ours was loaded, or `null` when the store agrees with us. */
  private async storedTokenIfNewer(current: ITokenSet): Promise<ITokenSet | null> {
    const stored = await this._store.load();
    const isNewer =
      stored != null &&
      stored.accessToken !== current.accessToken &&
      stored.expiresAt > current.expiresAt &&
      stored.refreshToken != null;
    return isNewer ? stored : null;
  }

  /** Mobile: FusionAuth redirects to `appauth://…`, which the OS hands to the app as a URL. */
  private listenForAppUrls(): void {
    if (this._listening) {
      return;
    }
    this._listening = true;
    void App.addListener('appUrlOpen', event => this._ngZone.run(() => void this.onAppUrl(event.url)));
    void Browser.addListener('browserFinished', () => this.onBrowserClosed());
  }

  private async onAppUrl(url: string): Promise<void> {
    if (url.startsWith(this.redirectUri)) {
      this.markCallbackReceived();
      const result = await this.completeSignIn(url);
      await this.closeBrowser();
      if (this._roundTrip) {
        this._roundTrip.resolve(result);
      } else if (result.kind === 'signedIn') {
        this._unsolicitedSignIn$.next();
      }
    } else if (url.startsWith(this.postLogoutRedirectUri)) {
      this.markCallbackReceived();
      await this.closeBrowser();
      this._roundTrip?.resolve('done');
    }
  }

  //#endregion

  //#region the system browser round trip (mobile)

  /**
   * Opens the system browser and settles with the callback's outcome — minutes later if the user is
   * slow — or with `cancelled` when the browser was closed without a callback.
   */
  private async roundTrip(url: string): Promise<RoundTripOutcome> {
    // A logout that starts while a login is still in the browser (the refresh of an old token was
    // rejected meanwhile) must not orphan that login: `login()` would never settle and the login
    // button would stay disabled until the app restarts.
    this._roundTrip?.resolve('cancelled');
    const roundTrip: IRoundTrip = { callbackReceived: false, resolve: () => undefined };
    const outcome = new Promise<RoundTripOutcome>(resolve => (roundTrip.resolve = resolve));
    this._roundTrip = roundTrip;
    try {
      await Browser.open({ url, windowName: '_self' });
      return await outcome;
    } catch (error) {
      return { kind: 'failed', error };
    } finally {
      // only our own slot: a cancelled round trip settles after its successor took the slot
      if (this._roundTrip === roundTrip) {
        this._roundTrip = null;
      }
    }
  }

  private markCallbackReceived(): void {
    if (this._roundTrip) {
      this._roundTrip.callbackReceived = true;
    }
  }

  /** The browser also closes after a successful callback; only a close without one is a cancel. */
  private onBrowserClosed(): void {
    const roundTrip = this._roundTrip;
    if (!roundTrip) {
      return;
    }
    setTimeout(() => {
      if (!roundTrip.callbackReceived) {
        roundTrip.resolve('cancelled');
      }
    }, BROWSER_CLOSE_GRACE_MS);
  }

  private async closeBrowser(): Promise<void> {
    // Android closes the custom tab itself when the app is brought back by the redirect
    if (this._platform.is('android')) {
      return;
    }
    try {
      await Browser.close();
    } catch {
      // nothing to close
    }
  }

  //#endregion

  private savePendingSignIn(pending: IPendingSignIn): void {
    localStorage.setItem(PENDING_SIGN_IN_KEY, JSON.stringify(pending));
  }

  private takePendingSignIn(): IPendingSignIn | null {
    const raw = localStorage.getItem(PENDING_SIGN_IN_KEY);
    localStorage.removeItem(PENDING_SIGN_IN_KEY);
    try {
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }
}

function hasAuthorizationResponse(url: string): boolean {
  try {
    const params = new URL(url).searchParams;
    return params.has('state') && (params.has('code') || params.has('error'));
  } catch {
    return false;
  }
}

/** The low-cardinality cause of a failed sign-in: the identity provider's code, or one of ours. */
function signInFailureCause(error: unknown): string {
  if (error instanceof OAuthError) {
    return error.code;
  }
  if (error instanceof OAuthUnavailableError) {
    return 'identity_provider_unavailable';
  }
  switch (errorMessageOf(error)) {
    case 'state does not match':
      return 'state_mismatch';
    case 'no sign-in is pending':
      return 'no_pending_sign_in';
    case 'unexpected callback':
      return 'unexpected_callback';
    default:
      return 'unknown';
  }
}
